import { defineStore } from "pinia";
import type { RouteLocationNormalized } from "vue-router";
import { useSessionStore } from "@/stores/session-store";

/**
 * Session-scoped developer/debug mode. Off by default in every environment; latched on for the tab when
 * a route carries `?debug=1`. Not persisted — closing the tab clears it, so the flag can't quietly ride
 * along on a production user's device forever. The query param is the *activator*; this store is the
 * *state*, so debug UI survives navigation to routes that don't repeat the param.
 *
 * Gate any developer-only affordance (e.g. the run-history "Simulate run" tool) on `debugToolsVisible`,
 * which requires BOTH the flag AND a real SuperAdmin session — the flag alone is a soft key, so a
 * curious user who learns it still can't reveal the tools in production.
 */
export const useDebugStore = defineStore("debug", {
  state: () => ({
    debugMode: false,
  }),

  getters: {
    // The flag reveals debug UI only for a real internal SuperAdmin. Mirrors the router's internal-route
    // rule exactly: a SuperAdmin viewing a foreign org carries the synthetic 'SuperAdminView' role and
    // is excluded. This is the gate views should use, not `debugMode` on its own.
    debugToolsVisible(state): boolean {
      const session = useSessionStore();
      return state.debugMode && session.isSuperAdmin && session.role !== "SuperAdminView";
    },
  },

  actions: {
    // Latch on when ?debug=1 is seen. Never turns off from the URL — dropping the param on a later
    // navigation shouldn't hide the menu mid-session; a reload/new tab is the way out.
    activateFromQuery(route: RouteLocationNormalized) {
      if (route.query.debug === "1") this.debugMode = true;
    },
  },
});
